import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Mail, Phone, ArrowRight } from 'lucide-react';
import { siteConfig, footerLinks } from '../data/mock';
import { sectors } from '../data/sectors';
import NewsletterSignup from './NewsletterSignup';

const TikTokIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.71a8.19 8.19 0 004.76 1.52V6.78a4.83 4.83 0 01-1-.09z" />
  </svg>
);

const Footer = () => {
  return (
    <footer data-testid="main-footer">
      {/* Social Icons Row */}
      <div style={{ background: 'var(--dark)', padding: '32px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 48 }}>
          {[
            { icon: <Instagram size={28} />, url: siteConfig.social.instagram, name: 'Instagram' },
            { icon: <Facebook size={28} />, url: siteConfig.social.facebook, name: 'Facebook' },
            { icon: <Linkedin size={28} />, url: siteConfig.social.linkedin, name: 'LinkedIn' },
            { icon: <TikTokIcon />, url: siteConfig.social.tiktok, name: 'TikTok' },
          ].map((s, i) => (
            <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
              aria-label={`Blue Cactus Digital on ${s.name}`} title={`Blue Cactus Digital on ${s.name}`}
              style={{ color: 'rgba(255,255,255,0.7)', transition: 'color 0.25s ease', display: 'flex', alignItems: 'center' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--hot-pink)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
            >{s.icon}</a>
          ))}
        </div>
      </div>

      {/* "Get in ToUch" Footer CTA */}
      <div style={{ background: 'var(--dark)', padding: '40px 0 60px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center', padding: '0 48px' }}>
          <h4 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            color: 'var(--white)',
            marginBottom: 24,
            textTransform: 'uppercase',
          }}>
            GET IN TOUCH
          </h4>
          <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.6)', marginBottom: 28, lineHeight: 1.8 }}>
            Working in health, social care or wellbeing? Let's talk about how we can help you reach the people who need you.
          </p>
          <Link to="/contact" className="btn-pink-filled" data-testid="footer-contact-btn">
            <ArrowRight size={14} /> CONTACT US
          </Link>
        </div>
      </div>

      {/* Newsletter capture band */}
      <div style={{ background: '#141428', padding: '40px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 48px' }}>
          <NewsletterSignup theme="dark" testId="footer-newsletter" />
        </div>
      </div>

      {/* Footer Bottom */}
      <div style={{ background: '#111', padding: '36px 0' }}>
        <div className="footer-grid" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px', display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: 40 }}>
          {/* Brand */}
          <div>
            <img src={siteConfig.logo} alt="Blue Cactus Digital" style={{ height: 28, marginBottom: 16, filter: 'brightness(0) invert(1)' }} />
            <p style={{ fontSize: '14px', lineHeight: 1.7, maxWidth: 300, color: 'rgba(255,255,255,0.5)' }}>
              Specialist digital marketing. Helping people live happier, healthier lives.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '12px', letterSpacing: '0.08em', color: 'var(--white)', marginBottom: 16, textTransform: 'uppercase' }}>QUICK LINKS</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {footerLinks.map((link) => (
                <Link key={link.path} to={link.path} data-testid={`footer-link-${link.path.replace(/\//g, '')}`}
                  style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', transition: 'color 0.25s ease' }}
                  onMouseEnter={(e) => { e.target.style.color = 'var(--hot-pink)'; }}
                  onMouseLeave={(e) => { e.target.style.color = 'rgba(255,255,255,0.5)'; }}
                >{link.label}</Link>
              ))}
            </div>
          </div>

          {/* Sectors */}
          <div>
            <h4 style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '12px', letterSpacing: '0.08em', color: 'var(--white)', marginBottom: 16, textTransform: 'uppercase' }}>SECTORS</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sectors.map((s) => (
                <Link key={s.slug} to={`/sectors/${s.slug}`} data-testid={`footer-sector-${s.slug}`}
                  style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', transition: 'color 0.25s ease' }}
                  onMouseEnter={(e) => { e.target.style.color = 'var(--hot-pink)'; }}
                  onMouseLeave={(e) => { e.target.style.color = 'rgba(255,255,255,0.5)'; }}
                >{s.navLabel}</Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '12px', letterSpacing: '0.08em', color: 'var(--white)', marginBottom: 16, textTransform: 'uppercase' }}>CONTACT</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <a href={`mailto:${siteConfig.email}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '14px', color: 'rgba(255,255,255,0.5)', transition: 'color 0.25s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--hot-pink)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
              >
                <Mail size={14} />{siteConfig.email}
              </a>
              <a href={`tel:${siteConfig.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '14px', color: 'rgba(255,255,255,0.5)', transition: 'color 0.25s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--hot-pink)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
              >
                <Phone size={14} />{siteConfig.phone}
              </a>
            </div>
          </div>
          {/* Tools & Resources - spans all columns */}
          <div style={{ gridColumn: '1 / -1', marginTop: 12, paddingTop: 28, borderTop: '1px solid rgba(255,255,255,0.08)' }} data-testid="footer-tools">
            <h4 style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '12px', letterSpacing: '0.08em', color: 'var(--white)', marginBottom: 16, textTransform: 'uppercase' }}>TOOLS &amp; RESOURCES</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '10px 32px' }}>
              {[
                { path: '/google-ad-grant-for-charities', label: 'Free Charity Ad Grant' },
                { path: '/healthtech-demand-generation', label: 'HealthTech Pipeline Check' },
                { path: '/marketing-health-check', label: 'Free Health Check' },
                { path: '/resources', label: 'Free Resources' },
                { path: '/webinars', label: 'Free Webinars' },
                { path: '/charity-marketing-cost', label: 'Charity Marketing Cost' },
                { path: '/care-technology-marketing-cost', label: 'Care Tech Marketing Cost' },
                { path: '/marketing-roi-calculator', label: 'ROI Calculator' },
                { path: '/google-ad-grant-calculator', label: 'Ad Grant Calculator' },
                { path: '/marketing-pricing-comparison', label: 'Agency vs In-House' },
                { path: '/services/seo-for-charities', label: 'SEO for Charities' },
                { path: '/services/web-design-for-care-homes', label: 'Web Design for Care Homes' },
                { path: '/services/marketing-for-accountants', label: 'Marketing for Accountants' },
                { path: '/services/saas-demand-generation', label: 'SaaS Demand Generation' },
                { path: '/packages', label: 'Packages & Pricing' },
                { path: '/digital-marketing-case-studies', label: 'Case Studies' },
              ].map((link) => (
                <Link key={link.path} to={link.path} data-testid={`footer-tool-${link.path.replace(/\//g, '-')}`}
                  style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', transition: 'color 0.25s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--hot-pink)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
                >{link.label}</Link>
              ))}
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 1400, margin: '24px auto 0', padding: '16px 48px 0', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>&copy; {new Date().getFullYear()} Blue Cactus Digital. All rights reserved.</p>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <Link to="/privacy-policy" style={{ fontSize: '12px', fontFamily: 'var(--font-heading)', fontWeight: 400, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.4)', transition: 'color 0.25s ease', textTransform: 'uppercase' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
            >Privacy Policy</Link>
            <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: '12px', fontFamily: 'var(--font-heading)', fontWeight: 400, letterSpacing: '0.08em', color: 'var(--hot-pink)', transition: 'opacity 0.25s ease', textTransform: 'uppercase' }}
              onMouseEnter={(e) => { e.target.style.opacity = '0.7'; }}
              onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
            >BOOK A CALL</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
